"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.generateAggregation = exports.getSortPrql = exports.parseField = exports.isValueFormat = exports.DateFormats = exports.NumberFormats = exports.FloatFormats = void 0;
exports.referencedVariablesInExpression = referencedVariablesInExpression;
exports.parseExpressionReferences = parseExpressionReferences;
exports.translateExpressionToPrql = translateExpressionToPrql;
exports.translateToPrql = translateToPrql;
const lodash_1 = require("lodash");
const representation_1 = require("./representation");
const definition_1 = require("./definition");
const functions_1 = require("./functions");
const error_1 = require("./error");
exports.FloatFormats = ['percentge'];
exports.NumberFormats = ['percentage', 'eur', 'usd', 'gbp'];
exports.DateFormats = ['iso', 'date', 'time'];
const isValueFormat = (value) => {
    return exports.NumberFormats.includes(value) || exports.FloatFormats.includes(value) || exports.DateFormats.includes(value);
};
exports.isValueFormat = isValueFormat;
const parseField = (field) => {
    if (!(0, representation_1.isFieldNode)(field)) {
        throw new error_1.ArgumentError('Cannot parse field argument', field);
    }
    return field.path.join('.');
};
exports.parseField = parseField;
const parseLiteral = (literal) => {
    if (!(0, representation_1.isLiteralNode)(literal)) {
        throw new error_1.TypeError('Cannot parse literal argument', literal);
    }
    if (literal.type === 'String') {
        return wrapString(literal.value);
    }
    if (literal.type === 'Boolean') {
        return literal.value ? 'true' : 'false';
    }
    return literal.value;
};
const parseArgumentAtIndex = (functionNode, index) => {
    const arg = (0, lodash_1.nth)(functionNode.children, index);
    if (arg === undefined) {
        throw new error_1.InvalidFunctionError(`Cannot parse argument at position ${index}`, functionNode);
    }
    return arg;
};
const parseNumberLiteral = (literal) => {
    if (!(0, representation_1.isLiteralNode)(literal) || literal.type !== 'Number') {
        throw new error_1.TypeError('Cannot parse literal argument', literal);
    }
    return literal.value;
};
const parseStringLiteral = (literal) => {
    if (!(0, representation_1.isLiteralNode)(literal) || literal.type !== 'String') {
        throw new error_1.TypeError('Cannot parse literal argument', literal);
    }
    return literal.value;
};
const wrapString = (str) => `"${str}"`;
const parseArg = (fieldOrLiteral, context) => {
    if (fieldOrLiteral === undefined) {
        throw new Error('Cannot parse field or literal argument');
    }
    if ((0, representation_1.isNullNode)(fieldOrLiteral)) {
        return nullNodeToPrql(fieldOrLiteral).prql;
    }
    if ((0, lodash_1.isObject)(fieldOrLiteral)) {
        if (fieldOrLiteral.label === 'field') {
            return escapeKey((0, exports.parseField)(fieldOrLiteral));
        }
        if (fieldOrLiteral.label === 'literal') {
            return parseLiteral(fieldOrLiteral);
        }
        return `(${nodeToPrql(fieldOrLiteral, context).prql})`;
    }
    throw new Error('Cannot parse field or literal argument');
};
const wrapNodeForSString = (node, ctx) => {
    const res = nodeToPrql(node, ctx);
    if ((0, representation_1.isFunctionNode)(node) && node.name === 'prop') {
        const arg = parseArgumentAtIndex(node, 0);
        if ((0, representation_1.isFieldNode)(arg)) {
            const key = (0, exports.parseField)(arg);
            const field = ctx.fields.find((field) => field.key === key);
            if (field === undefined) {
                throw new error_1.ArgumentError(`Unknown column ${key}`, (0, lodash_1.first)(node.children) ?? node);
            }
            return { node, prql: `{${field.alias ?? field.key}}`, returnType: res.returnType };
        }
        else {
            const name = parseStringLiteral(arg);
            const field = ctx.fields.find((field) => field.name === name);
            if (field === undefined) {
                throw new error_1.ArgumentError(`Unknown column ${name}`, (0, lodash_1.first)(node.children) ?? node);
            }
            return { node, prql: `{${field.alias ?? field.key}}`, returnType: res.returnType };
        }
    }
    if ((0, representation_1.isFunctionNode)(node) && node.name === 'variable') {
        const variable = variableNodeToPrql(node, ctx);
        if (variable.returnType === definition_1.T.date) {
            return { node, prql: `'${variable.value}'`, returnType: variable.returnType };
        }
        return variable;
    }
    if ((0, representation_1.isFunctionNode)(node)) {
        throw new error_1.TypeError('Only prop or variable function is allowed in this context', node);
    }
    if ((0, representation_1.isFieldNode)(node)) {
        return { node, prql: `{${res.prql}}`, returnType: res.returnType };
    }
    return { node, prql: res.prql, returnType: res.returnType };
};
const coalesceNumericNode = (node, context) => {
    const res = nodeToPrql(node, context);
    if ((0, definition_1.isNumeric)(res.returnType) && !((0, representation_1.isFunctionNode)(node) && node.name !== 'prop') && !(0, representation_1.isLiteralNode)(node)) {
        return { node, prql: `(${res.prql} ?? 0)`, returnType: res.returnType };
    }
    if ((0, representation_1.isLiteralNode)(node) || (0, representation_1.isFieldNode)(node)) {
        return { node, prql: res.prql, returnType: res.returnType };
    }
    return { node, prql: `(${res.prql})`, returnType: res.returnType };
};
const getSortPrql = (field, direction) => {
    const sortDirection = direction === 'desc' ? '-' : '';
    return sortDirection === '' ? field : `{-${field}}`;
};
exports.getSortPrql = getSortPrql;
const generateAggregation = (fn, node, ctx) => {
    const column = fn === 'count' ? '1' : parseArg((0, lodash_1.first)(node.children), ctx);
    const idx = fn === 'count' ? -1 : 0;
    // TODO: Handle missing params
    const aggregation = `derive {${ctx.key} = std.${fn} ${column}}`;
    if (fn === 'count' && node.children.length === 0) {
        return aggregation;
    }
    if (fn === 'count' && node.children.length === 1) {
        return `window rows:.. (${aggregation} | group ${parseArg(node.children.at(0), ctx)})`;
    }
    if (node.children.length === 1) {
        return aggregation;
    }
    // Prop, Group scenario
    if (node.children.length === 2) {
        return `window rows:.. (${aggregation} | group ${parseArg(node.children.at(1), ctx)})`;
    }
    // TODO: Parse sort direction properly from args, using parseArg resulted in '"desc"'
    const sort = (0, exports.getSortPrql)(parseArg(node.children.at(idx + 1), ctx), parseStringLiteral(parseArgumentAtIndex(node, idx + 2)));
    const rangeStartArg = parseArg(node.children.at(idx + 3), ctx);
    const rangeStart = rangeStartArg === 'null' ? '' : rangeStartArg ?? '';
    const rangeEndArg = parseArg(node.children.at(idx + 4), ctx);
    const rangeEnd = rangeEndArg === 'null' ? '' : rangeEndArg ?? '';
    const range = `rows:${rangeStart}..${rangeEnd}`;
    const group = node.children.at(idx + 5) !== undefined ? parseArg(node.children.at(idx + 5), ctx) : undefined;
    return `window ${range} (sort ${sort} | ${aggregation}${group !== undefined ? ` | group ${group}` : ''})`;
};
exports.generateAggregation = generateAggregation;
const fnHandlers = {
    eq: (node, ctx) => {
        const def = functions_1.functions.eq;
        const [left, right] = node.children;
        return { prql: `(${parseArg(left, ctx)} ${def.operator} ${parseArg(right, ctx)}) ?? false` };
    },
    neq: (node, ctx) => ({
        prql: `(${parseArg((0, lodash_1.nth)(node.children, 0), ctx)} != ${parseArg((0, lodash_1.nth)(node.children, 1), ctx)}) ?? true`,
    }),
    gt: (node, ctx) => ({
        prql: `(${parseArg((0, lodash_1.nth)(node.children, 0), ctx)} > ${parseArg((0, lodash_1.nth)(node.children, 1), ctx)}) ?? false`,
    }),
    gte: (node, ctx) => ({
        prql: `(${parseArg((0, lodash_1.nth)(node.children, 0), ctx)} >= ${parseArg((0, lodash_1.nth)(node.children, 1), ctx)}) ?? false`,
    }),
    lt: (node, ctx) => ({
        prql: `(${parseArg((0, lodash_1.nth)(node.children, 0), ctx)} < ${parseArg((0, lodash_1.nth)(node.children, 1), ctx)}) ?? false`,
    }),
    lte: (node, ctx) => ({
        prql: `(${parseArg((0, lodash_1.nth)(node.children, 0), ctx)} <= ${parseArg((0, lodash_1.nth)(node.children, 1), ctx)}) ?? false`,
    }),
    and: (node, ctx) => ({ prql: node.children.map((child) => parseArg(child, ctx)).join(' && ') }),
    or: (node, ctx) => ({ prql: node.children.map((child) => parseArg(child, ctx)).join(' || ') }),
    not: (node, ctx) => ({ prql: `!${parseArg((0, lodash_1.first)(node.children), ctx)}` }),
    case: (node, ctx) => {
        return {
            prql: `case [${(0, lodash_1.chunk)(node.children, 2)
                .map(([condition, value]) => value !== undefined
                ? `${parseArg(condition, ctx)} => ${parseArg(value, ctx)}`
                : `true => ${parseArg(condition, ctx)}`)
                .join(', ')}]`,
        };
    },
    coalesce: (node, ctx) => ({ prql: node.children.map((child) => parseArg(child, ctx)).join(' ?? ') }),
    concat: (node, ctx) => {
        const concatStr = node.children
            .map((child) => {
            if ((0, representation_1.isFunctionNode)(child) && child.name === 'prop') {
                return `{${functionNodeToPrql(child, ctx).prql}}`;
            }
            if ((0, representation_1.isFunctionNode)(child) && child.name === 'variable') {
                return variableNodeToPrql(child, ctx).value;
            }
            if ((0, representation_1.isLiteralNode)(child)) {
                if (child.type === 'String') {
                    return escapeQuotes(child.value);
                }
                return child.value;
            }
            if ((0, representation_1.isFieldNode)(child)) {
                return `{${(0, exports.parseField)(child)}}`;
            }
            return '';
        })
            .join('');
        return { prql: `f"${concatStr}"` };
    },
    contains: (node, ctx) => {
        const [field, search] = node.children;
        return { prql: `${parseArg(field, ctx)} | text.lower | text.contains (${parseLiteral(search)} | text.lower)` };
    },
    split_part: (node, ctx) => {
        const [str, delimiter, position] = node.children;
        return {
            prql: `split_part ${parseArg(str, ctx)} '${parseStringLiteral(delimiter)}' ${parseNumberLiteral(position)}`,
        };
    },
    add: (node, ctx) => {
        const dateNode = node.children.find((child) => nodeToPrql(child, ctx).returnType === definition_1.T.date);
        if (dateNode !== undefined) {
            const intervalNode = node.children.find((child) => child !== dateNode);
            if (intervalNode === undefined || nodeToPrql(intervalNode, ctx).returnType !== definition_1.T.interval) {
                throw new error_1.TypeError('Expected interval argument', intervalNode ?? node);
            }
            return { prql: `date_add ${parseArg(dateNode, ctx)} ${parseArg(intervalNode, ctx)}` };
        }
        return { prql: node.children.map((child) => coalesceNumericNode(child, ctx).prql).join(' + ') };
    },
    sub: (node, ctx) => {
        const dateNode = node.children.find((child) => nodeToPrql(child, ctx).returnType === definition_1.T.date);
        const intervalNode = node.children.find((child) => nodeToPrql(child, ctx).returnType === definition_1.T.interval);
        if (dateNode !== undefined && intervalNode !== undefined) {
            return { prql: `date_sub ${parseArg(dateNode, ctx)} ${parseArg(intervalNode, ctx)}` };
        }
        return { prql: node.children.map((child) => coalesceNumericNode(child, ctx).prql).join(' - ') };
    },
    mul: (node, ctx) => ({ prql: node.children.map((child) => parseArg(child, ctx)).join(' * ') }),
    div: (node, ctx) => ({
        prql: `${parseArg(parseArgumentAtIndex(node, 0), ctx)} / (nullif ${parseArg(parseArgumentAtIndex(node, 1), ctx)} 0)`,
    }),
    max: (node, ctx) => ({
        prql: `s"greatest(${node.children.map((child) => wrapNodeForSString(child, ctx).prql).join(', ')})"`,
    }),
    min: (node, ctx) => ({
        prql: `s"least(${node.children.map((child) => wrapNodeForSString(child, ctx).prql).join(', ')})"`,
    }),
    ceil: (node, ctx) => ({ prql: `math.ceil ${wrapNegativeValue(parseArg((0, lodash_1.first)(node.children), ctx))}` }),
    floor: (node, ctx) => ({ prql: `math.floor ${wrapNegativeValue(parseArg((0, lodash_1.first)(node.children), ctx))}` }),
    round: (node, ctx) => {
        const precision = node.children.length === 1 ? 0 : parseArg(parseArgumentAtIndex(node, 1), ctx);
        const value = parseArg(parseArgumentAtIndex(node, 0), ctx);
        return { prql: `math.round ${wrapNegativeValue(precision)} ${wrapNegativeValue(value)}` };
    },
    date_diff: (node, ctx) => {
        const [start, end] = node.children;
        const unit = parseStringLiteral(parseArgumentAtIndex(node, 2)).replace(/s$/, '');
        if (!definition_1.DateDiffUnits.includes(unit)) {
            throw new error_1.TypeError(`Invalid unit "${unit}"`, node);
        }
        return { prql: `timestamp_diff ${parseArg(start, ctx)} ${parseArg(end, ctx)} "${unit}"` };
    },
    start_of_year: (node, ctx) => ({ prql: `date_trunc "year" ${parseArg((0, lodash_1.first)(node.children), ctx)}` }),
    end_of_year: (node, ctx) => ({
        prql: `(date_sub (date_add (date_trunc "year" ${parseArg((0, lodash_1.first)(node.children), ctx)}) 1years) 1seconds)`,
    }),
    start_of_quarter: (node, ctx) => ({ prql: `date_trunc "quarter" ${parseArg((0, lodash_1.first)(node.children), ctx)}` }),
    end_of_quarter: (node, ctx) => ({
        prql: `(date_sub (date_add (date_trunc "quarter" ${parseArg((0, lodash_1.first)(node.children), ctx)}) 3months) 1seconds)`,
    }),
    start_of_month: (node, ctx) => ({ prql: `date_trunc "month" ${parseArg((0, lodash_1.first)(node.children), ctx)}` }),
    end_of_month: (node, ctx) => ({
        prql: `(date_sub (date_add (date_trunc "month" ${parseArg((0, lodash_1.first)(node.children), ctx)}) 1months) 1seconds)`,
    }),
    start_of_week: (node, ctx) => ({ prql: `date_trunc "week" ${parseArg((0, lodash_1.first)(node.children), ctx)}` }),
    end_of_week: (node, ctx) => ({
        prql: `(date_sub (date_add (date_trunc "week" ${parseArg((0, lodash_1.first)(node.children), ctx)}) 1weeks) 1seconds)`,
    }),
    start_of_day: (node, ctx) => ({ prql: `date_trunc "day" ${parseArg((0, lodash_1.first)(node.children), ctx)}` }),
    end_of_day: (node, ctx) => ({
        prql: `(date_sub (date_add (date_trunc "day" ${parseArg((0, lodash_1.first)(node.children), ctx)}) 1days) 1seconds)`,
    }),
    start_of_hour: (node, ctx) => ({ prql: `date_trunc "hour" ${parseArg((0, lodash_1.first)(node.children), ctx)}` }),
    end_of_hour: (node, ctx) => ({
        prql: `(date_sub (date_add (date_trunc "hour" ${parseArg((0, lodash_1.first)(node.children), ctx)}) 1hours) 1seconds)`,
    }),
    start_of_minute: (node, ctx) => ({ prql: `date_trunc "minute" ${parseArg((0, lodash_1.first)(node.children), ctx)}` }),
    end_of_minute: (node, ctx) => ({
        prql: `(date_add (date_trunc "minute" ${parseArg((0, lodash_1.first)(node.children), ctx)}) 59seconds)`,
    }),
    percentage: (node, ctx) => {
        const [field, group] = node.children;
        if (group === undefined) {
            return { prql: `percentage ${parseArg(field, ctx)}` };
        }
        return { prql: `group_percentage ${parseArg(field, ctx)} ${parseArg(group, ctx)}` };
    },
    int: (node, ctx) => {
        const firstNode = (0, lodash_1.first)(node.children);
        const res = firstNode !== undefined ? nodeToPrql(firstNode, ctx) : undefined;
        const returnType = res?.returnType === definition_1.T.boolean ? 't_smallint' : 't_integer';
        return { prql: `${parseArg((0, lodash_1.first)(node.children), ctx)} | cast ${returnType}` };
    },
    float: (node, ctx) => ({ prql: `${parseArg((0, lodash_1.first)(node.children), ctx)} | cast t_float` }),
    str: (node, ctx) => ({ prql: `${parseArg((0, lodash_1.first)(node.children), ctx)} | cast t_text` }),
    bool: (node, ctx) => ({ prql: `${parseArg((0, lodash_1.first)(node.children), ctx)} | cast t_boolean` }),
    date: (node, ctx) => ({ prql: `${parseArg((0, lodash_1.first)(node.children), ctx)} | cast t_timestamptz` }),
    now: () => ({ prql: `now` }),
    interval: (node, ctx) => {
        const durationNode = parseArgumentAtIndex(node, 0);
        const unitNode = parseArgumentAtIndex(node, 1);
        const durationValue = (0, representation_1.isFunctionNode)(durationNode) && durationNode.name === 'variable'
            ? (() => {
                const durationVariable = variableNodeToPrql(durationNode, ctx);
                if (durationVariable.returnType !== definition_1.T.number || !(0, lodash_1.isNumber)(durationVariable.value)) {
                    throw new error_1.TypeError('Invalid duration variable type', durationNode);
                }
                return durationVariable.value;
            })()
            : parseNumberLiteral(durationNode);
        const [duration, unit] = parseStringLiteral(unitNode).startsWith('week')
            ? [durationValue * 7, 'day']
            : [durationValue, parseStringLiteral(unitNode)];
        if (definition_1.DurationUnits.includes(unit)) {
            return { prql: `${duration}${unit}s` };
        }
        if (definition_1.DurationUnits.includes(unit.replace(/s$/, ''))) {
            return { prql: `${duration}${unit}` };
        }
        throw new error_1.TypeError(`Invalid duration unit ${unit}`, durationNode);
    },
    extract: (node, ctx) => {
        const unitNode = parseArgumentAtIndex(node, 1);
        const unit = parseStringLiteral(unitNode);
        if (definition_1.DurationUnits.includes(unit)) {
            return { prql: `${parseArg((0, lodash_1.first)(node.children), ctx)} | interval_${unit}s` };
        }
        throw new error_1.TypeError(`Invalid duration unit ${unit}`, unitNode);
    },
    year: (node, ctx) => ({ prql: `date_extract ${wrapString(node.name)} ${parseArg((0, lodash_1.first)(node.children), ctx)}` }),
    month: (node, ctx) => ({ prql: `date_extract ${wrapString(node.name)} ${parseArg((0, lodash_1.first)(node.children), ctx)}` }),
    week: (node, ctx) => ({ prql: `date_extract ${wrapString(node.name)} ${parseArg((0, lodash_1.first)(node.children), ctx)}` }),
    day: (node, ctx) => ({ prql: `date_extract ${wrapString(node.name)} ${parseArg((0, lodash_1.first)(node.children), ctx)}` }),
    hour: (node, ctx) => ({ prql: `date_extract ${wrapString(node.name)} ${parseArg((0, lodash_1.first)(node.children), ctx)}` }),
    minute: (node, ctx) => ({ prql: `date_extract ${wrapString(node.name)} ${parseArg((0, lodash_1.first)(node.children), ctx)}` }),
    second: (node, ctx) => ({ prql: `date_extract ${wrapString(node.name)} ${parseArg((0, lodash_1.first)(node.children), ctx)}` }),
    day_of_week: (node, ctx) => ({
        prql: `date_extract ${wrapString(node.name)} ${parseArg((0, lodash_1.first)(node.children), ctx)}`,
    }),
    json_get_text: (node, ctx) => ({
        prql: `json_get_text ${parseArg((0, lodash_1.first)(node.children), ctx)} ${parseArg(parseArgumentAtIndex(node, 1), ctx)}`,
    }),
    json_get_number: (node, ctx) => ({
        prql: `json_get_number ${parseArg((0, lodash_1.first)(node.children), ctx)} ${parseArg(parseArgumentAtIndex(node, 1), ctx)}`,
    }),
    json_get_integer: (node, ctx) => ({
        prql: `json_get_integer ${parseArg((0, lodash_1.first)(node.children), ctx)} ${parseArg(parseArgumentAtIndex(node, 1), ctx)}`,
    }),
    json_get_float: (node, ctx) => ({
        prql: `json_get_float ${parseArg((0, lodash_1.first)(node.children), ctx)} ${parseArg(parseArgumentAtIndex(node, 1), ctx)}`,
    }),
    json_get_timestamptz: (node, ctx) => ({
        prql: `json_get_timestamptz ${parseArg((0, lodash_1.first)(node.children), ctx)} ${parseArg(parseArgumentAtIndex(node, 1), ctx)}`,
    }),
    json_get_boolean: (node, ctx) => ({
        prql: `json_get_boolean ${parseArg((0, lodash_1.first)(node.children), ctx)} ${parseArg(parseArgumentAtIndex(node, 1), ctx)}`,
    }),
    prop: (node, ctx) => {
        const arg = parseArgumentAtIndex(node, 0);
        if ((0, representation_1.isFieldNode)(arg)) {
            const key = (0, exports.parseField)(arg);
            const field = ctx.fields.find((field) => field.key === key);
            if (field === undefined) {
                throw new error_1.ArgumentError(`Unknown column ${key}`, (0, lodash_1.first)(node.children) ?? node);
            }
            return {
                prql: escapeKeyInAlias(field.alias) ?? `${arg.path.length === 1 ? 'this.' : ''}${escapeKey(field.key)}`,
            };
        }
        else {
            const name = parseStringLiteral(arg);
            const field = ctx.fields.find((field) => field.name === name);
            if (field === undefined) {
                throw new error_1.ArgumentError(`Unknown column ${name}`, (0, lodash_1.first)(node.children) ?? node);
            }
            return { prql: escapeKeyInAlias(field.alias) ?? `this.${escapeKey(field.key)}` };
        }
    },
    offset: (node, ctx) => {
        const column = parseArg((0, lodash_1.first)(node.children), ctx);
        const index = Number(parseArg(node.children.at(1), ctx));
        const method = index < 0 ? 'lag' : 'lead';
        const offset = Math.abs(index);
        const prql = `derive {${ctx.key} = ${method} ${offset} ${column}}`;
        const sortDirectionNode = node.children.at(3);
        const sortDirection = sortDirectionNode !== undefined && (0, representation_1.isLiteralNode)(sortDirectionNode)
            ? parseStringLiteral(sortDirectionNode)
            : 'asc';
        const sortPropertyNode = parseArgumentAtIndex(node, 2);
        if ((0, representation_1.isNullNode)(sortPropertyNode)) {
            throw new error_1.ArgumentError('Invalid sort property', node);
        }
        const sort = (0, exports.getSortPrql)(parseArg(sortPropertyNode, ctx), sortDirection);
        const sortPrql = `sort ${sort} | ${prql}`;
        if (node.children.length === 5) {
            const group = node.children.at(4) !== undefined ? parseArg(node.children.at(4), ctx) : undefined;
            return { prql: `window rows:.. (${sortPrql}${group !== undefined ? ` | group ${group}` : ''})` };
        }
        return { prql: sortPrql };
    },
    variable: (node, ctx) => ({ prql: variableNodeToPrql(node, ctx).prql }),
    prql: (node) => {
        const [prql] = node.children;
        if (!(0, representation_1.isLiteralNode)(prql)) {
            throw new Error('Expected PRQL string');
        }
        return { prql: prql.value.toString() };
    },
    avg: (node, ctx) => ({ prql: (0, exports.generateAggregation)('average', node, ctx) }),
    sum: (node, ctx) => ({ prql: (0, exports.generateAggregation)('sum', node, ctx) }),
    count: (node, ctx) => ({ prql: (0, exports.generateAggregation)('count', node, ctx) }),
    len: (node, ctx) => {
        const child = (0, lodash_1.first)(node.children);
        if (child === undefined) {
            throw new error_1.InputError('len function expects at least one argument', node);
        }
        let str = parseArg(child, ctx);
        if ((0, representation_1.isFunctionNode)(child) && child.name === 'prop') {
            str = functionNodeToPrql(child, ctx).prql;
        }
        if ((0, representation_1.isFunctionNode)(child) && child.name === 'variable') {
            str = `'${variableNodeToPrql(child, ctx).value}'`;
        }
        if ((0, representation_1.isFieldNode)(child)) {
            str = `{${(0, exports.parseField)(child)}}`;
        }
        if ((0, representation_1.isLiteralNode)(child)) {
            if (child.type === 'String') {
                str = `'${child.value}'`;
            }
        }
        return { prql: `s"length(${str})"` };
    },
    row_number: (node, ctx) => {
        const aggregation = `derive {${ctx.key} = std.row_number 1}`;
        if (node.children.length === 0) {
            return { prql: aggregation };
        }
        if (node.children.length === 1) {
            return { prql: `window rows:.. (${aggregation} | group ${parseArg(node.children.at(0), ctx)})` };
        }
        const sort = (0, exports.getSortPrql)(parseArg(node.children.at(0), ctx), parseStringLiteral(parseArgumentAtIndex(node, 1)));
        const group = node.children.at(2) !== undefined ? parseArg(node.children.at(2), ctx) : undefined;
        return { prql: `window rows:.. (sort ${sort} | ${aggregation}${group !== undefined ? ` | group ${group}` : ''})` };
    },
    format: (node, ctx) => {
        const valueNode = parseArgumentAtIndex(node, 0);
        const { returnType: type } = nodeToPrql(valueNode, ctx);
        const formatNode = parseArgumentAtIndex(node, 1);
        const format = parseStringLiteral(formatNode);
        if (!(0, exports.isValueFormat)(format)) {
            throw new error_1.ArgumentError(`Invalid format ${format}`, formatNode);
        }
        if (exports.FloatFormats.includes(format) && type !== definition_1.T.float && type !== definition_1.T.number) {
            throw new error_1.ArgumentError(`Format ${format} requires a floating-point number`, formatNode);
        }
        if ((0, definition_1.isNumeric)(type) && !exports.NumberFormats.includes(format)) {
            throw new error_1.ArgumentError(`Invalid number format ${format}`, formatNode);
        }
        if (type === definition_1.T.date && !exports.DateFormats.includes(format)) {
            throw new error_1.ArgumentError(`Invalid date format ${format}`, formatNode);
        }
        return { prql: `${parseArg(valueNode, ctx)}`, format: format };
    },
};
const nullNodeToPrql = (node) => {
    return { node, prql: 'null', returnType: null };
};
const literalNodeToPrql = (node) => {
    return { node, prql: valueToPrql(node.value, node.type), returnType: node.type };
};
const escapeQuotes = (value) => value.replaceAll('"', '\\"');
const escapeKey = (key) => (key === 'type' ? `\`${key}\`` : key);
const escapeKeyInAlias = (alias) => alias !== undefined
    ? alias
        .split('.')
        .map((part, index, array) => (index === array.length - 1 ? escapeKey(part) : part))
        .join('.')
    : alias;
// Negative numbers in PRQL are better to be surrounded with parentheses, such as (-10) instead of -10.
// Most PRQL experssions will error otherwise. Everything else will be passed through this function as is.
const wrapNegativeValue = (value) => ((0, lodash_1.isNumber)(value) && value < 0 ? `(${value})` : value);
const valueToPrql = (value, type) => {
    if (type === definition_1.T.string) {
        return wrapString(escapeQuotes(value.toString()));
    }
    if (type === definition_1.T.boolean) {
        return value ? 'true' : 'false';
    }
    if (type === definition_1.T.date) {
        return `@${value.toString()}`;
    }
    return value.toString();
};
const fieldNodeToPrql = (node, context) => {
    const field = context.fields.find((field) => field.key === (0, exports.parseField)(node));
    return { node, prql: field?.alias ?? field?.key ?? (0, exports.parseField)(node), returnType: (0, definition_1.parseT)(field?.type ?? null) };
};
const functionNodeToPrql = (node, context) => {
    const handler = (0, lodash_1.get)(fnHandlers, node.name);
    const def = (0, lodash_1.get)(functions_1.functions, node.name);
    if (handler === undefined || def === undefined) {
        throw new error_1.InvalidFunctionError(`Unknown function ${node.name}`, node);
    }
    const evaluatedArguments = node.children.map((child) => nodeToPrql(child, context));
    const argumentTypes = evaluatedArguments.map((e) => e.returnType);
    const result = (0, definition_1.findMatchingSignatures)(evaluatedArguments, def);
    if (result.type === 'failure') {
        throw new error_1.InvalidArgumentError('Function called with incompatible arguments', node, result.closestMatch);
    }
    const returnTypes = (0, lodash_1.uniq)(result.signatures.map((s) => s.returnType));
    // When there are multiple return types with unknown argument types, we cannot determine the return type.
    // With multiple return types but known argument types it comes down to the order in which function signatures are
    // defined, preferrably from most specific to least specific.
    const returnType = (returnTypes.length > 1 && argumentTypes.some(lodash_1.isNil) ? null : (0, lodash_1.first)(returnTypes)) ?? null;
    const { prql, format } = handler(node, context);
    // TODO: Should be included in handler function
    if (node.name === 'prop') {
        let field;
        const arg = parseArgumentAtIndex(node, 0);
        if ((0, representation_1.isFieldNode)(arg)) {
            const key = (0, exports.parseField)(arg);
            field = context.fields.find((field) => field.key === key);
        }
        else {
            const name = parseStringLiteral(arg);
            field = context.fields.find((field) => field.name === name);
        }
        return { node, prql, returnType: (0, definition_1.parseT)(field?.type ?? null) };
    }
    if (node.name === 'variable') {
        return variableNodeToPrql(node, context);
    }
    return { node, prql, returnType, format };
};
const nodeToPrql = (node, context) => {
    if ((0, representation_1.isLiteralNode)(node)) {
        return literalNodeToPrql(node);
    }
    if ((0, representation_1.isFieldNode)(node)) {
        return fieldNodeToPrql(node, context);
    }
    if ((0, representation_1.isNullNode)(node)) {
        return nullNodeToPrql(node);
    }
    if ((0, representation_1.isFunctionNode)(node)) {
        return functionNodeToPrql(node, context);
    }
    throw new error_1.TypeError(`Invalid expression`, node);
};
const variableNodeToPrql = (node, context) => {
    if (!(0, representation_1.isFunctionNode)(node)) {
        throw new error_1.TypeError('Expected function node', node);
    }
    const variableName = parseStringLiteral(parseArgumentAtIndex(node, 0));
    const variable = context.variables.find((variable) => variable.key === variableName);
    if (variable === undefined) {
        throw new error_1.ArgumentError(`Unknown variable ${variableName}`, (0, lodash_1.first)(node.children) ?? node);
    }
    return {
        node,
        prql: valueToPrql(variable.value, (0, definition_1.parseT)(variable.type)),
        value: variable.value,
        returnType: (0, definition_1.parseT)(variable.type),
    };
};
const nodeToVariables = (node) => {
    if ((0, representation_1.isFunctionNode)(node)) {
        if (node.name === 'variable') {
            const variableName = parseStringLiteral(parseArgumentAtIndex(node, 0));
            return [variableName];
        }
        return node.children.flatMap(nodeToVariables);
    }
    return [];
};
const nodeToFields = (node) => {
    if ((0, representation_1.isFunctionNode)(node)) {
        if (node.name === 'prop') {
            const arg = parseArgumentAtIndex(node, 0);
            if ((0, representation_1.isLiteralNode)(arg)) {
                if (arg.type !== 'String') {
                    throw new error_1.TypeError('Expected string literal in prop', arg);
                }
                const propName = arg.value;
                return [propName];
            }
            else if ((0, representation_1.isFieldNode)(arg)) {
                const propName = (0, exports.parseField)(arg);
                return [propName];
            }
            throw new error_1.TypeError('Unexpected node in prop', arg);
        }
        return node.children.flatMap(nodeToFields);
    }
    return [];
};
function referencedVariablesInExpression(expression) {
    const [node, ...rest] = (0, representation_1.parseSource)(expression);
    if (rest.length > 0) {
        throw new error_1.InputError('Unexpected expression', rest[0]);
    }
    return [...new Set(nodeToVariables(node))];
}
function parseExpressionReferences(expression) {
    const [node, ...rest] = (0, representation_1.parseSource)(expression);
    if (rest.length > 0) {
        throw new error_1.InputError('Unexpected expression', rest[0]);
    }
    const fields = [...new Set(nodeToFields(node))];
    const variables = [...new Set(nodeToVariables(node))];
    return {
        fields,
        variables,
    };
}
function translateExpressionToPrql(expression, context, returnNode = false) {
    const [node, ...rest] = (0, representation_1.parseSource)(expression);
    if (rest.length > 0) {
        throw new error_1.InputError('Unexpected expression', rest[0]);
    }
    const { prql, returnType, format } = nodeToPrql(node, context);
    return returnNode ? { prql, returnType, node, format } : { prql, returnType, format };
}
// TODO: Derive wrapping could be moved back to api engine, to achieve that we should return a unique identifier here for the expression field
function translateToPrql(expression, context) {
    const { prql, returnType, node, format } = translateExpressionToPrql(expression, context, true);
    const isAggregationFn = node !== undefined && (0, representation_1.isFunctionNode)(node) && ['sum', 'count', 'avg', 'offset', 'row_number'].includes(node.name);
    if (!isAggregationFn && context.key !== undefined) {
        const wrappedPrql = `derive {${escapeKey(context.key)} = ${prql}}`;
        return { prql: wrappedPrql, returnType };
    }
    return { prql, returnType, format };
}
//# sourceMappingURL=index.js.map